<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const HERO_VIDEO_SRC = '/videos/hero-1.mp4';
/** 可见比例低于此值视为离开首屏，暂停视频以节省解码/合成开销 */
const HERO_PLAY_RATIO = 0.55;

const heroSectionRef = useTemplateRef('heroSectionRef');
const heroVideoRef = useTemplateRef('heroVideoRef');

const { t } = useI18n();
const openContact = inject('openContact', () => {});
const pageLoading = inject('pageLoading', ref(false));
const heroReady = computed(() => !pageLoading.value);
const heroInView = ref(true);
const hasPlayedOnce = ref(false);

let visibilityObserver = null;

function pauseIfLoading() {
  const el = heroVideoRef.value;
  if (!el || heroReady.value) return;
  el.pause();
}

function pauseHeroVideo() {
  heroVideoRef.value?.pause();
}

async function startHeroVideo() {
  if (!heroReady.value || !heroInView.value) return;
  const el = heroVideoRef.value;
  if (!el) return;

  // 等首屏渐显后再 play，避免被浏览器拦截
  await nextTick();
  await new Promise((resolve) => requestAnimationFrame(resolve));
  if (!heroReady.value || !heroInView.value) return;

  try {
    if (!hasPlayedOnce.value && el.currentTime > 0.05) {
      el.currentTime = 0;
      await new Promise((resolve) => {
        el.addEventListener('seeked', resolve, { once: true });
      });
    }
    if (!el.paused) return;
    await el.play();
    hasPlayedOnce.value = true;
  } catch {
    // 自动播放被拦截时，等 canplay 重试
  }
}

function syncHeroPlayback() {
  if (!heroReady.value) {
    pauseIfLoading();
    return;
  }
  if (heroInView.value) startHeroVideo();
  else pauseHeroVideo();
}

function onVideoCanPlay() {
  syncHeroPlayback();
}

watch(heroReady, syncHeroPlayback);

onMounted(() => {
  const el = heroSectionRef.value;
  if (!el || typeof IntersectionObserver === 'undefined') return;

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      heroInView.value = entry.isIntersecting && entry.intersectionRatio >= HERO_PLAY_RATIO;
      syncHeroPlayback();
    },
    { threshold: [0, 0.25, HERO_PLAY_RATIO, 0.75, 1] },
  );
  visibilityObserver.observe(el);
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
  visibilityObserver = null;
});

function enterCls(delayClass = '') {
  return [
    'transition-[opacity,transform] duration-700 ease-out',
    delayClass,
    heroReady.value ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
  ];
}
</script>

<template>
  <section
    ref="heroSectionRef"
    id="hero"
    class="relative isolate -mt-18 flex w-full flex-col overflow-hidden pt-18 text-white sm:min-h-screen"
  >
    <!-- 视频背景 -->
    <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-[#050b16]" aria-hidden="true">
      <video
        ref="heroVideoRef"
        :src="HERO_VIDEO_SRC"
        class="absolute inset-0 size-full object-cover"
        muted
        loop
        playsinline
        preload="auto"
        @canplay="onVideoCanPlay"
        @loadeddata="pauseIfLoading"
        @playing="pauseIfLoading"
      />
      <!-- 顶部渐变，保证导航栏可读 -->
      <div class="absolute inset-0 bg-gradient-to-b from-[#050b16]/70 from-0% via-[#050b16]/25 via-10% to-transparent to-24%" />
      <!-- 底部渐变，保证左下角文案可读 -->
      <div class="absolute inset-0 bg-gradient-to-t from-[#050b16]/75 from-0% via-[#050b16]/20 via-18% to-transparent to-38%" />
    </div>

    <!-- 精简内容：左下角 -->
    <div class="relative z-10 flex w-full flex-col justify-end px-5 pb-10 pt-28 sm:mt-auto sm:min-h-[calc(100vh-4.5rem)] sm:px-8 sm:pb-12 sm:pt-0 lg:px-10 lg:pb-14 xl:px-12">
      <div class="max-w-lg text-left">
        <p :class="['text-[0.6875rem] font-semibold uppercase tracking-[0.22em] text-white/60', enterCls()]">
          {{ t('hero.eyebrow') }}
        </p>

        <h1 :class="['mt-3 text-3xl font-semibold leading-[1.15] tracking-[-0.03em] sm:text-4xl lg:text-[2.75rem]', enterCls('delay-75')]">
          {{ t('hero.title') }}
        </h1>

        <p :class="['mt-3 max-w-md text-sm leading-relaxed text-white/65 sm:text-[0.9375rem]', enterCls('delay-150')]">
          {{ t('hero.subtitle') }}
        </p>

        <div :class="['mt-6 flex flex-wrap items-center gap-3', enterCls('delay-225')]">
          <button
            type="button"
            class="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
            @click="openContact()"
          >
            {{ t('hero.ctaContact') }}
          </button>
          <a
            href="#product"
            class="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-5 py-2.5 text-sm font-medium text-white/85 transition hover:border-white/30 hover:bg-white/5 hover:text-white"
          >
            {{ t('hero.ctaProduct') }}
            <svg class="size-3.5 opacity-70" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
